import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';

import { UserCheck, UserX, CreditCard, AlertCircle } from 'lucide-react';

const ServicesTab = ({
  services,
  servicePrices,
  setServicePrices,
  toggleService,
  updateServicePrice,
  expiredUsers,
  handleRemoveExpiredUsers,
  hasPermission
}) => {
  return (
          <div className="space-y-6">
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center">
                  <UserCheck className="w-4 h-4 mr-2" />
                  Manage Services
                </CardTitle>
                <CardDescription className="text-gray-600">
                  Enable or disable services for new subscriptions
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Options Only */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h3 className="font-semibold text-gray-900">Options Only</h3>
                      <p className="text-sm text-gray-600">Intraday options trading service</p>
                    </div>
                    <button
                      onClick={() => toggleService('options_only')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        services.options_only ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          services.options_only ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Complete Package */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h3 className="font-semibold text-gray-900">Complete Package</h3>
                      <p className="text-sm text-gray-600">Options + Futures + Equity trading service</p>
                    </div>
                    <button
                      onClick={() => toggleService('complete_package')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        services.complete_package ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          services.complete_package ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Equity Only */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h3 className="font-semibold text-gray-900">Equity Only</h3>
                      <p className="text-sm text-gray-600">Equity trading service</p>
                    </div>
                    <button
                      onClick={() => toggleService('equity_only')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        services.equity_only ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          services.equity_only ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Workshop */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h3 className="font-semibold text-gray-900">Workshop</h3>
                      <p className="text-sm text-gray-600">Complete Setup on Intraday Option Trading</p>
                    </div>
                    <button
                      onClick={() => toggleService('workshop')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        services.workshop ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          services.workshop ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Index Options (Educational Purpose Only) */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h3 className="font-semibold text-gray-900">Index Options (Educational Purpose Only)</h3>
                      <p className="text-sm text-gray-600">Monthly educational sessions on Index Options</p>
                    </div>
                    <button
                      data-testid="toggle-index-options-educational-btn"
                      onClick={() => toggleService('index_options_educational')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        services.index_options_educational ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          services.index_options_educational ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Automated Alerts 1 Day */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h3 className="font-semibold text-gray-900">Automated Alerts (1 Day)</h3>
                      <p className="text-sm text-gray-600">1 trading day alert service</p>
                    </div>
                    <button
                      onClick={() => toggleService('automated_alerts_1day')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        services.automated_alerts_1day ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          services.automated_alerts_1day ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Automated Alerts 3 Day */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h3 className="font-semibold text-gray-900">Automated Alerts (3 Day)</h3>
                      <p className="text-sm text-gray-600">3 trading days alert service</p>
                    </div>
                    <button
                      onClick={() => toggleService('automated_alerts_3day')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        services.automated_alerts_3day ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          services.automated_alerts_3day ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Automated Alerts 5 Day */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h3 className="font-semibold text-gray-900">Automated Alerts (5 Day)</h3>
                      <p className="text-sm text-gray-600">5 trading days alert service</p>
                    </div>
                    <button
                      onClick={() => toggleService('automated_alerts_5day')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        services.automated_alerts_5day ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          services.automated_alerts_5day ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Pricing Management */}
            <Card className="bg-white">
              <CardHeader>
                <CardTitle className="text-gray-900 flex items-center">
                  <CreditCard className="w-4 h-4 mr-2" />
                  Service Pricing
                </CardTitle>
                <CardDescription className="text-gray-600">
                  Update pricing for all subscription services
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Options Only */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">Options Only</h3>
                      <p className="text-sm text-gray-600">Intraday options trading service</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600 font-medium">₹</span>
                      <Input
                        type="number"
                        value={servicePrices.options_only}
                        onChange={(e) => setServicePrices({...servicePrices, options_only: (e.target.value === "" ? "" : Number(e.target.value))})}
                        className="w-24 border-gray-300"
                      />
                      <Button
                        size="sm"
                        onClick={() => updateServicePrice('options_only', servicePrices.options_only)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Update
                      </Button>
                    </div>
                  </div>

                  {/* Complete Package */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">Complete Package</h3>
                      <p className="text-sm text-gray-600">Options + Futures + Equity trading</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600 font-medium">₹</span>
                      <Input
                        type="number"
                        value={servicePrices.complete_package}
                        onChange={(e) => setServicePrices({...servicePrices, complete_package: (e.target.value === "" ? "" : Number(e.target.value))})}
                        className="w-24 border-gray-300"
                      />
                      <Button
                        size="sm"
                        onClick={() => updateServicePrice('complete_package', servicePrices.complete_package)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Update
                      </Button>
                    </div>
                  </div>

                  {/* Equity Only */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">Equity Only</h3>
                      <p className="text-sm text-gray-600">Equity trading service</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600 font-medium">₹</span>
                      <Input
                        type="number"
                        value={servicePrices.equity_only}
                        onChange={(e) => setServicePrices({...servicePrices, equity_only: (e.target.value === "" ? "" : Number(e.target.value))})}
                        className="w-24 border-gray-300"
                      />
                      <Button
                        size="sm"
                        onClick={() => updateServicePrice('equity_only', servicePrices.equity_only)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Update
                      </Button>
                    </div>
                  </div>

                  {/* Options Only - 3 Months */}
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">Options Only - 3 Months</h3>
                      <p className="text-sm text-gray-600">3-month subscription for options trading</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600 font-medium">₹</span>
                      <Input
                        type="number"
                        value={servicePrices.options_only_3month}
                        onChange={(e) => setServicePrices({...servicePrices, options_only_3month: (e.target.value === "" ? "" : Number(e.target.value))})}
                        className="w-24 border-gray-300"
                      />
                      <Button
                        size="sm"
                        onClick={() => updateServicePrice('options_only_3month', servicePrices.options_only_3month)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Update
                      </Button>
                    </div>
                  </div>

                  {/* Complete Package - 3 Months */}
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">Complete Package - 3 Months</h3>
                      <p className="text-sm text-gray-600">3-month subscription for complete package</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600 font-medium">₹</span>
                      <Input
                        type="number"
                        value={servicePrices.complete_package_3month}
                        onChange={(e) => setServicePrices({...servicePrices, complete_package_3month: (e.target.value === "" ? "" : Number(e.target.value))})}
                        className="w-24 border-gray-300"
                      />
                      <Button
                        size="sm"
                        onClick={() => updateServicePrice('complete_package_3month', servicePrices.complete_package_3month)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Update
                      </Button>
                    </div>
                  </div>

                  {/* Equity Only - 3 Months */}
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">Equity Only - 3 Months</h3>
                      <p className="text-sm text-gray-600">3-month subscription for equity trading</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600 font-medium">₹</span>
                      <Input
                        type="number"
                        value={servicePrices.equity_only_3month}
                        onChange={(e) => setServicePrices({...servicePrices, equity_only_3month: (e.target.value === "" ? "" : Number(e.target.value))})}
                        className="w-24 border-gray-300"
                      />
                      <Button
                        size="sm"
                        onClick={() => updateServicePrice('equity_only_3month', servicePrices.equity_only_3month)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Update
                      </Button>
                    </div>
                  </div>

                  {/* Workshop */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">Workshop</h3>
                      <p className="text-sm text-gray-600">Complete Setup on Intraday Option Trading</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600 font-medium">₹</span>
                      <Input
                        type="number"
                        value={servicePrices.workshop}
                        onChange={(e) => setServicePrices({...servicePrices, workshop: (e.target.value === "" ? "" : Number(e.target.value))})}
                        className="w-24 border-gray-300"
                      />
                      <Button
                        size="sm"
                        onClick={() => updateServicePrice('workshop', servicePrices.workshop)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Update
                      </Button>
                    </div>
                  </div>

                  {/* Index Options (Educational Purpose Only) */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">Index Options (Educational Purpose Only)</h3>
                      <p className="text-sm text-gray-600">Monthly educational sessions on Index Options</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600 font-medium">₹</span>
                      <Input
                        data-testid="price-index-options-educational-input"
                        type="number"
                        value={servicePrices.index_options_educational}
                        onChange={(e) => setServicePrices({...servicePrices, index_options_educational: (e.target.value === "" ? "" : Number(e.target.value))})}
                        className="w-24 border-gray-300"
                      />
                      <Button
                        data-testid="update-price-index-options-educational-btn"
                        size="sm"
                        onClick={() => updateServicePrice('index_options_educational', servicePrices.index_options_educational)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Update
                      </Button>
                    </div>
                  </div>

                  {/* Automated Alerts 1 Day */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">Automated Alerts (1 Day)</h3>
                      <p className="text-sm text-gray-600">1 trading day alert service</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600 font-medium">₹</span>
                      <Input
                        type="number"
                        value={servicePrices.automated_alerts_1day}
                        onChange={(e) => setServicePrices({...servicePrices, automated_alerts_1day: (e.target.value === "" ? "" : Number(e.target.value))})}
                        className="w-24 border-gray-300"
                      />
                      <Button
                        size="sm"
                        onClick={() => updateServicePrice('automated_alerts_1day', servicePrices.automated_alerts_1day)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Update
                      </Button>
                    </div>
                  </div>

                  {/* Automated Alerts 3 Day */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">Automated Alerts (3 Day)</h3>
                      <p className="text-sm text-gray-600">3 trading days alert service</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600 font-medium">₹</span>
                      <Input
                        type="number"
                        value={servicePrices.automated_alerts_3day}
                        onChange={(e) => setServicePrices({...servicePrices, automated_alerts_3day: (e.target.value === "" ? "" : Number(e.target.value))})}
                        className="w-24 border-gray-300"
                      />
                      <Button
                        size="sm"
                        onClick={() => updateServicePrice('automated_alerts_3day', servicePrices.automated_alerts_3day)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Update
                      </Button>
                    </div>
                  </div>

                  {/* Automated Alerts 5 Day */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">Automated Alerts (5 Day)</h3>
                      <p className="text-sm text-gray-600">5 trading days alert service</p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600 font-medium">₹</span>
                      <Input
                        type="number"
                        value={servicePrices.automated_alerts_5day}
                        onChange={(e) => setServicePrices({...servicePrices, automated_alerts_5day: (e.target.value === "" ? "" : Number(e.target.value))})}
                        className="w-24 border-gray-300"
                      />
                      <Button
                        size="sm"
                        onClick={() => updateServicePrice('automated_alerts_5day', servicePrices.automated_alerts_5day)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        Update
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-amber-50 border-amber-200">
              <CardHeader>
                <CardTitle className="text-amber-900 flex items-center">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Important Note
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-amber-800 text-sm">
                  Disabling a service will prevent new users from subscribing to it. 
                  Existing active subscriptions will continue to work normally.
                </p>
              </CardContent>
            </Card>

          </div>

  );
};

export default ServicesTab;
